import { useState, useCallback, useEffect, useMemo } from 'react'
import type { ArbeitgeberFunnelData } from '../../lib/types'
import { EMPTY_AG_FUNNEL_DATA } from '../../lib/types'
import { CATEGORIES, agSlidersForCategory } from '../../lib/matching/catalog'

const STORAGE_KEY = 'medilane_ag_funnel_state'

export interface AGSubQuestion {
  phase: number
  phaseLabel: string
  phaseIcon: string
  questionId: string
  questionLabel: string
}

const q = (
  phase: number,
  phaseLabel: string,
  phaseIcon: string,
  questionId: string,
  questionLabel: string,
): AGSubQuestion => ({ phase, phaseLabel, phaseIcon, questionId, questionLabel })

export const AG_PHASE_COUNT = 7

export const AG_SUB_QUESTIONS: AGSubQuestion[] = [
  // Phase 1: Einrichtung
  q(1, 'Einrichtung', '🏢', 'einrichtung_basis', 'Ihre Einrichtung'),
  q(1, 'Einrichtung', '🏢', 'standort', 'Standort'),
  q(1, 'Einrichtung', '🏢', 'kontakt', 'Ihr Ansprechpartner'),

  // Phase 2: Bedarf an Pflegekräften
  q(2, 'Bedarf', '🎯', 'bedarf_qualifikationen', 'Welche Pflegekräfte suchen Sie?'),
  q(2, 'Bedarf', '🎯', 'bedarf_anforderung', 'Anforderungen an Bewerber'),
  q(2, 'Bedarf', '🎯', 'bedarf_arbeitszeit', 'Arbeitszeit-Modelle'),

  // Phase 3: Vergütung & Benefits
  q(3, 'Vergütung & Benefits', '💰', 'verguetung', 'Vergütung & Benefits'),

  // Phase 4: Dienstplan & Fakten
  q(4, 'Dienstplan & Fakten', '📅', 'schicht_anforderung', 'Für welche Dienste suchen Sie besonders Unterstützung?'),
  q(4, 'Dienstplan & Fakten', '📅', 'dienstplan_fakten', 'Wie läuft die Dienstplanung wirklich?'),
  q(4, 'Dienstplan & Fakten', '📅', 'fakten', 'Konkrete Rahmenbedingungen'),

  // Phase 5: Arbeitsbedingungen (nur Kategorien mit AG-rated Slidern)
  ...CATEGORIES
    .filter((c) => agSlidersForCategory(c.id).length > 0)
    .map((c) => q(5, 'Arbeitsbedingungen', '⭐', `cat_${c.id}`, c.label)),

  // Phase 6: Onboarding & Service
  q(6, 'Onboarding & Service', '🧭', 'onboarding', 'Einarbeitung & Wechselbegleitung'),

  // Phase 7: Abschluss
  q(7, 'Abschluss', '✅', 'consent', 'Fast geschafft!'),
]

interface StoredState {
  questionIndex: number
  data: ArbeitgeberFunnelData
  savedAt: string
}

export function useArbeitgeberFunnel() {
  const [questionIndex, setQuestionIndex] = useState(0)
  const [data, setData] = useState<ArbeitgeberFunnelData>(EMPTY_AG_FUNNEL_DATA)
  const [hasRecovery, setHasRecovery] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const currentQuestion = AG_SUB_QUESTIONS[questionIndex]
  const totalQuestions = AG_SUB_QUESTIONS.length
  const currentPhase = currentQuestion.phase

  const phaseQuestions = useMemo(
    () => AG_SUB_QUESTIONS.filter((x) => x.phase === currentPhase),
    [currentPhase],
  )
  const phaseSubIndex = phaseQuestions.findIndex((x) => x.questionId === currentQuestion.questionId)
  const phaseSubTotal = phaseQuestions.length
  const phaseProgress = (currentPhase / AG_PHASE_COUNT) * 100

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const parsed: StoredState = JSON.parse(saved)
        const daysSince = (Date.now() - new Date(parsed.savedAt).getTime()) / 86400000
        if (daysSince < 7 && parsed.data.unternehmensname) setHasRecovery(true)
      }
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    if (submitted) return
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ questionIndex, data, savedAt: new Date().toISOString() }),
      )
    } catch {
      /* ignore */
    }
  }, [questionIndex, data, submitted])

  const recoverState = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const parsed: StoredState = JSON.parse(saved)
        setData({ ...EMPTY_AG_FUNNEL_DATA, ...parsed.data })
        setQuestionIndex(Math.min(parsed.questionIndex, AG_SUB_QUESTIONS.length - 1))
        setHasRecovery(false)
      }
    } catch {
      /* ignore */
    }
  }, [])

  const dismissRecovery = useCallback(() => {
    setHasRecovery(false)
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  const updateField = useCallback(
    <K extends keyof ArbeitgeberFunnelData>(field: K, value: ArbeitgeberFunnelData[K]) => {
      setData((prev) => ({ ...prev, [field]: value }))
    },
    [],
  )

  const updateFields = useCallback((fields: Partial<ArbeitgeberFunnelData>) => {
    setData((prev) => ({ ...prev, ...fields }))
  }, [])

  const setSlider = useCallback((sliderId: string, value: number) => {
    setData((prev) => ({
      ...prev,
      slider_antworten: { ...prev.slider_antworten, [sliderId]: value },
    }))
  }, [])

  const next = useCallback(() => {
    setQuestionIndex((p) => Math.min(p + 1, totalQuestions - 1))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [totalQuestions])

  const prev = useCallback(() => {
    setQuestionIndex((p) => Math.max(p - 1, 0))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const goToPhase = useCallback(
    (phase: number) => {
      const idx = AG_SUB_QUESTIONS.findIndex((x) => x.phase === phase)
      if (idx >= 0 && idx <= questionIndex) {
        setQuestionIndex(idx)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    [questionIndex],
  )

  const markSubmitted = useCallback(() => {
    setSubmitted(true)
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  const reset = useCallback(() => {
    setData(EMPTY_AG_FUNNEL_DATA)
    setQuestionIndex(0)
    setSubmitted(false)
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  return {
    questionIndex,
    currentQuestion,
    currentPhase,
    phaseSubIndex,
    phaseSubTotal,
    phaseProgress,
    totalQuestions,
    data,
    hasRecovery,
    submitted,
    isFirstQuestion: questionIndex === 0,
    isLastQuestion: questionIndex === totalQuestions - 1,
    updateField,
    updateFields,
    setSlider,
    next,
    prev,
    goToPhase,
    recoverState,
    dismissRecovery,
    markSubmitted,
    reset,
  }
}

export type AGFunnelHook = ReturnType<typeof useArbeitgeberFunnel>
